import { deleteJson, getJson, postJson, putJson, uploadForm } from './client'
import type { DraftView, ExportedMessage, FeedbackView, HistoryView, JobView, MailExportFormat, MailboxView, RewriteView, ScenarioIndexView, ScenarioMessagesView, ScenarioView, UploadSummary, VoiceProfileView } from '../types/voice'

// One function per route on the voice surface. Owner-scoped: the session IS the mailbox owner, so
// no route carries a user id. Binds unchanged to a standalone Scout.Api or a Black Manta spoke.
const voice = (spoke: string) => `/api/spokes/${encodeURIComponent(spoke)}/scout/voice`

export const voiceApi = {
  mailbox: (spoke: string) => getJson<MailboxView>(`${voice(spoke)}/mailbox`),
  connect: (spoke: string, catalogType: string) => postJson<{ authorizeUrl: string; state: string }>(`${voice(spoke)}/mailbox/connect`, { catalogType }),
  import: (spoke: string, address: string, displayName: string | null, messages: ExportedMessage[]) =>
    postJson<MailboxView>(`${voice(spoke)}/mailbox/import`, { address, displayName, messages }),
  formats: (spoke: string) => getJson<MailExportFormat[]>(`${voice(spoke)}/mailbox/formats`),
  upload: (spoke: string, file: File, address: string, displayName: string | null, onProgress?: (f: number) => void, via?: string) => {
    const form = new FormData()
    form.append('file', file, file.name)
    form.append('address', address)
    if (displayName) form.append('displayName', displayName)
    if (via) form.append('via', via)   // 'superhuman': the export of the account under a Superhuman client
    return uploadForm<UploadSummary>(`${voice(spoke)}/mailbox/upload`, form, onProgress)
  },
  revoke: (spoke: string) => deleteJson<void>(`${voice(spoke)}/mailbox`),
  build: (spoke: string, lookbackMonths?: number) => postJson<JobView>(`${voice(spoke)}/build`, { lookbackMonths }),
  scan: (spoke: string, maxMessages?: number) => postJson<JobView>(`${voice(spoke)}/scan`, { maxMessages }),
  job: (spoke: string, jobId: string) => getJson<JobView>(`${voice(spoke)}/jobs/${encodeURIComponent(jobId)}`),
  profile: (spoke: string) => getJson<VoiceProfileView>(`${voice(spoke)}/profile`),
  editDescriptor: (spoke: string, scenario: string, summary: string | null, neverSay: string[] | null) =>
    putJson<ScenarioView>(`${voice(spoke)}/profile/${encodeURIComponent(scenario)}/descriptor`, { summary, neverSay }),
  draft: (spoke: string, scenario: string | null, brief: string, recipientName: string | null) =>
    postJson<DraftView>(`${voice(spoke)}/draft`, { scenario, brief, recipientName }),
  feedback: (spoke: string, draftId: string, finalText: string) =>
    postJson<FeedbackView>(`${voice(spoke)}/drafts/${encodeURIComponent(draftId)}/feedback`, { finalText }),
  history: (spoke: string) => getJson<HistoryView>(`${voice(spoke)}/history`),
  // seeing into one scenario at a time (REQ-061), and rewriting an example (REQ-062)
  scenarios: (spoke: string) => getJson<ScenarioIndexView>(`${voice(spoke)}/scenarios`),
  // audience and intent as their own path segments: a scenario key holds slashes, and IIS rejects a
  // segment carrying %2F before the app sees it
  scenarioMessages: (spoke: string, scenario: string, page: number, take: number) => {
    const [audience, intent] = scenario.split('/').slice(-2)
    return getJson<ScenarioMessagesView>(
      `${voice(spoke)}/scenarios/${encodeURIComponent(audience)}/${encodeURIComponent(intent)}/messages?page=${page}&take=${take}`)
  },
  rewriteExemplar: (spoke: string, messageId: string, text: string) =>
    postJson<RewriteView>(`${voice(spoke)}/exemplars/${encodeURIComponent(messageId)}/rewrite`, { text }),
  clearRewrite: (spoke: string, messageId: string) =>
    deleteJson<RewriteView>(`${voice(spoke)}/exemplars/${encodeURIComponent(messageId)}/rewrite`),
}
